import { apiClient } from '@/lib/api';
import { jsonToImpedanceExec } from '@/lib/protobuf';
import type { MeasurementProfile } from '@/types/protobuf';
import { useAuthStore } from '@/store';

export interface ProfileSummary {
  id: string;
  name: string;
  testType: string;
  status: string;
  isDefault: boolean;
}

/**
 * Profile API service
 */
export const profileService = {
  /**
   * Get all active profiles (light summary for dropdowns).
   * Passes the user's defaultLabId so lab-specific profiles are included
   * alongside global ones.
   */
  async getProfiles(status = 'active'): Promise<ProfileSummary[]> {
    const { user } = useAuthStore.getState();
    const params: Record<string, string> = { status };
    if (user?.defaultLabId) params.labId = user.defaultLabId;
    const response = await apiClient.get<{ items: ProfileSummary[] }>('/api/v1/profiles', { params });
    return response.data.items;
  },

  /**
   * Get the default measurement profile
   * This profile will be auto-selected in the wizard
   */
  async getDefaultProfile(): Promise<MeasurementProfile> {
    const response = await apiClient.get<MeasurementProfile>('/api/v1/profiles/default');
    const profile = response.data;
    // The backend returns impedanceExecJson as a JSON *string* (not an inline object).
    // Parse it first, then convert enum strings to protobuf numeric values.
    const rawJson: Record<string, unknown> =
      typeof profile.impedanceExecJson === 'string'
        ? JSON.parse(profile.impedanceExecJson as unknown as string)
        : (profile.impedanceExecJson as unknown as Record<string, unknown>);
    return {
      ...profile,
      impedanceExecJson: jsonToImpedanceExec(rawJson),
    };
  },
};
